
import { AppNode, ProcessDefinition, WorkflowNodeData } from '../types';
import { Edge } from '../types';

export interface BpmnImportResult {
    processes: ProcessDefinition[];
    warnings: string[];
}

// ─── Type Mappings ────────────────────────────────────────────────────────────

const TASK_TYPE_MAP: Record<string, string | null> = {
    userTask: 'UserTask',
    task: 'UserTask',
    manualTask: 'UserTask',
    receiveTask: 'UserTask',
    scriptTask: 'ScriptTask',
    serviceTask: 'ServiceTaskREST',
    sendTask: 'ServiceTaskREST',
    businessRuleTask: 'ServiceTaskRules',
    subProcess: 'SubProcess',
    callActivity: 'CallActivity',
};

const GATEWAY_TYPE_MAP: Record<string, string | null> = {
    exclusiveGateway: 'XorGateway',
    parallelGateway: 'ParallelGateway',
    inclusiveGateway: 'InclusiveGateway',
    complexGateway: 'XorGateway',
    eventBasedGateway: null, // disabled in PSS
};

const FLOW_ELEMENT_NAMES = new Set([
    'startEvent', 'endEvent',
    'task', 'userTask', 'scriptTask', 'serviceTask', 'sendTask', 'receiveTask', 'manualTask', 'businessRuleTask',
    'exclusiveGateway', 'parallelGateway', 'inclusiveGateway', 'complexGateway', 'eventBasedGateway',
    'subProcess', 'callActivity',
    'intermediateCatchEvent', 'intermediateThrowEvent',
    'boundaryEvent',
]);

// ─── Event Type Detection ─────────────────────────────────────────────────────

type EventBase = 'start' | 'end' | 'intermediateCatch' | 'intermediateThrow' | 'boundary';

function detectEventType(el: Element, base: EventBase): string | null {
    const childLocalNames = Array.from(el.children).map(c => c.localName);

    switch (base) {
        case 'start':
            if (childLocalNames.includes('messageEventDefinition')) return 'MessageStartEvent';
            if (childLocalNames.includes('timerEventDefinition')) return 'TimerStartEvent';
            if (childLocalNames.includes('signalEventDefinition')) return 'SignalStartEvent';
            return 'BlankStartEvent';

        case 'end':
            if (childLocalNames.includes('errorEventDefinition')) return 'ErrorEndEvent';
            if (childLocalNames.includes('terminateEventDefinition')) return 'TerminateEndEvent';
            if (childLocalNames.includes('messageEventDefinition')) return 'MessageEndEvent';
            if (childLocalNames.includes('signalEventDefinition')) return 'SignalEndEvent';
            return 'BlankEndEvent';

        case 'intermediateCatch':
            if (childLocalNames.includes('messageEventDefinition')) return 'IntermediateCatchMessageEvent';
            if (childLocalNames.includes('timerEventDefinition')) return 'IntermediateCatchTimerEvent';
            if (childLocalNames.includes('signalEventDefinition')) return 'IntermediateCatchSignalEvent';
            return null;

        case 'intermediateThrow':
            if (childLocalNames.includes('messageEventDefinition')) return 'IntermediateThrowMessageEvent';
            if (childLocalNames.includes('signalEventDefinition')) return 'IntermediateThrowSignalEvent';
            return null;

        case 'boundary':
            if (childLocalNames.includes('errorEventDefinition')) return 'ErrorBoundaryEvent';
            if (childLocalNames.includes('messageEventDefinition')) return 'MessageBoundaryEvent';
            if (childLocalNames.includes('signalEventDefinition')) return 'SignalBoundaryEvent';
            if (childLocalNames.includes('timerEventDefinition')) return 'TimerBoundaryEvent';
            return null;
    }
}

// ─── Diagram Shape / Position Index ──────────────────────────────────────────

interface BpmnBounds { x: number; y: number; width: number; height: number; }

const BPMNDI_NS = 'http://www.omg.org/spec/BPMN/20100524/DI';
const DC_NS = 'http://www.omg.org/spec/DD/20100524/DC';

function buildShapeIndex(doc: Document): Map<string, BpmnBounds> {
    const index = new Map<string, BpmnBounds>();
    const shapes = doc.getElementsByTagNameNS(BPMNDI_NS, 'BPMNShape');
    for (let i = 0; i < shapes.length; i++) {
        const shape = shapes[i];
        const elementId = shape.getAttribute('bpmnElement');
        if (!elementId) continue;
        const boundsEl = shape.getElementsByTagNameNS(DC_NS, 'Bounds')[0];
        if (boundsEl) {
            index.set(elementId, {
                x: parseFloat(boundsEl.getAttribute('x') || '0'),
                y: parseFloat(boundsEl.getAttribute('y') || '0'),
                width: parseFloat(boundsEl.getAttribute('width') || '100'),
                height: parseFloat(boundsEl.getAttribute('height') || '80'),
            });
        }
    }
    return index;
}

// ─── Data Extraction Helpers ──────────────────────────────────────────────────

function extractEventData(el: Element, pssType: string): Partial<WorkflowNodeData> {
    const data: Partial<WorkflowNodeData> = {};

    if (pssType.includes('Message')) {
        const def = Array.from(el.children).find(c => c.localName === 'messageEventDefinition');
        const ref = def?.getAttribute('messageRef');
        if (ref) data.messageName = ref;
    }

    if (pssType.includes('Signal')) {
        const def = Array.from(el.children).find(c => c.localName === 'signalEventDefinition');
        const ref = def?.getAttribute('signalRef');
        if (ref) data.signalName = ref;
    }

    if (pssType.includes('Timer')) {
        const def = Array.from(el.children).find(c => c.localName === 'timerEventDefinition');
        if (def) {
            const expr = def.querySelector('timeDuration') || def.querySelector('timeCycle') || def.querySelector('timeDate');
            if (expr?.textContent) data.timerDefinition = expr.textContent.trim();
        }
    }

    if (pssType.includes('Error')) {
        const def = Array.from(el.children).find(c => c.localName === 'errorEventDefinition');
        const ref = def?.getAttribute('errorRef');
        if (ref) data.errorMessage = ref;
    }

    return data;
}

function extractTaskData(el: Element, pssType: string): Partial<WorkflowNodeData> {
    const data: Partial<WorkflowNodeData> = {};

    if (pssType === 'ScriptTask') {
        const format = el.getAttribute('scriptFormat');
        if (format) data.scriptLanguage = format;
        const scriptEl = Array.from(el.children).find(c => c.localName === 'script');
        if (scriptEl?.textContent) data.script = scriptEl.textContent;
    }

    if (pssType === 'CallActivity') {
        const calledElement = el.getAttribute('calledElement');
        if (calledElement) data.calledElement = calledElement;
    }

    return data;
}

// ─── Process Parser ───────────────────────────────────────────────────────────

function parseProcess(
    processEl: Element,
    shapeIndex: Map<string, BpmnBounds>,
    warnings: string[]
): ProcessDefinition {
    const processId = processEl.getAttribute('id') || `bpmn_process_${Date.now()}`;
    const processName = processEl.getAttribute('name') || processEl.getAttribute('id') || 'Imported Process';
    const isSubProcess = processEl.localName === 'subProcess';

    const nodes: AppNode[] = [];
    const edges: Edge[] = [];
    const mappedNodeIds = new Set<string>();
    let autoLayoutCounter = 0;

    const flowElements = Array.from(processEl.children).filter(el => FLOW_ELEMENT_NAMES.has(el.localName));

    for (const el of flowElements) {
        const localName = el.localName;
        const bpmnId = el.getAttribute('id');
        if (!bpmnId) {
            warnings.push(`Skipped ${localName} element with no id attribute.`);
            continue;
        }

        const bpmnName = el.getAttribute('name') || bpmnId;
        let pssType: string | null = null;
        let extraData: Partial<WorkflowNodeData> = {};
        let parentNode: string | undefined = undefined;

        if (localName === 'startEvent') {
            pssType = detectEventType(el, 'start');
        } else if (localName === 'endEvent') {
            pssType = detectEventType(el, 'end');
        } else if (localName === 'intermediateCatchEvent') {
            pssType = detectEventType(el, 'intermediateCatch');
        } else if (localName === 'intermediateThrowEvent') {
            pssType = detectEventType(el, 'intermediateThrow');
        } else if (localName === 'boundaryEvent') {
            pssType = detectEventType(el, 'boundary');
            const attachedToRef = el.getAttribute('attachedToRef');
            if (attachedToRef) parentNode = attachedToRef;
            extraData.cancelActivity = el.getAttribute('cancelActivity') !== 'false';
            if (!el.getAttribute('cancelActivity') || el.getAttribute('cancelActivity') === 'false') {
                // Non-interrupting — note this in warnings; PSS only has interrupting variants
                if (el.getAttribute('cancelActivity') === 'false') {
                    warnings.push(`Non-interrupting boundary event '${bpmnId}' imported as interrupting (PSS does not support non-interrupting boundary events).`);
                }
            }
        } else if (Object.prototype.hasOwnProperty.call(GATEWAY_TYPE_MAP, localName)) {
            pssType = GATEWAY_TYPE_MAP[localName];
        } else if (Object.prototype.hasOwnProperty.call(TASK_TYPE_MAP, localName)) {
            pssType = TASK_TYPE_MAP[localName];
        }

        if (pssType === null) {
            warnings.push(`Skipped unsupported element: <${localName}> (id: ${bpmnId})`);
            continue;
        }

        // Extract type-specific data
        if (localName.includes('Event')) {
            Object.assign(extraData, extractEventData(el, pssType));
        }
        Object.assign(extraData, extractTaskData(el, pssType));

        // Documentation → description
        const docEl = Array.from(el.children).find(c => c.localName === 'documentation');
        if (docEl?.textContent?.trim()) extraData.description = docEl.textContent.trim();

        // Position from BPMNDiagram
        let position: { x: number; y: number };
        const bounds = shapeIndex.get(bpmnId);
        if (bounds) {
            if (parentNode) {
                // Make boundary event position relative to its host task
                const parentBounds = shapeIndex.get(parentNode);
                position = parentBounds
                    ? { x: bounds.x - parentBounds.x, y: bounds.y - parentBounds.y }
                    : { x: bounds.x, y: bounds.y };
            } else {
                position = { x: bounds.x, y: bounds.y };
            }
        } else {
            position = { x: (autoLayoutCounter % 8) * 220, y: Math.floor(autoLayoutCounter / 8) * 160 };
            autoLayoutCounter++;
            warnings.push(`No diagram position for '${bpmnId}', using auto-layout.`);
        }

        mappedNodeIds.add(bpmnId);

        const node: AppNode = {
            id: bpmnId,
            type: pssType,
            position,
            data: {
                label: bpmnName,
                ...extraData,
            },
            ...(parentNode ? { parentNode } : {}),
        };

        nodes.push(node);
    }

    // Sequence flows → edges
    const sequenceFlows = Array.from(processEl.children).filter(el => el.localName === 'sequenceFlow');

    for (const flow of sequenceFlows) {
        const flowId = flow.getAttribute('id');
        const sourceRef = flow.getAttribute('sourceRef');
        const targetRef = flow.getAttribute('targetRef');

        if (!flowId || !sourceRef || !targetRef) continue;
        // Skip edges where an endpoint was skipped (unsupported element)
        if (!mappedNodeIds.has(sourceRef) || !mappedNodeIds.has(targetRef)) continue;

        const conditionEl = Array.from(flow.children).find(c => c.localName === 'conditionExpression');
        const condition = conditionEl?.textContent?.trim() || undefined;
        const name = flow.getAttribute('name') || undefined;

        edges.push({
            id: flowId,
            source: sourceRef,
            target: targetRef,
            ...(name ? { label: name } : {}),
            ...(condition ? { data: { expression: condition } } : {}),
        } as Edge);
    }

    return { id: processId, name: processName, isSubProcess, nodes, edges };
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Parses a BPMN 2.0 XML string and converts it to the editor's ProcessDefinition format.
 */
export function importBpmnXml(xmlContent: string): BpmnImportResult {
    const warnings: string[] = [];

    const doc = new DOMParser().parseFromString(xmlContent, 'text/xml');

    const parseError = doc.querySelector('parsererror');
    if (parseError) {
        return { processes: [], warnings: [`XML parse error: ${parseError.textContent?.slice(0, 200)}`] };
    }

    const root = doc.documentElement;
    if (root.localName !== 'definitions') {
        return { processes: [], warnings: ['Not a valid BPMN 2.0 file: root element must be <definitions>.'] };
    }

    const shapeIndex = buildShapeIndex(doc);

    const processEls = Array.from(root.children).filter(el =>
        el.localName === 'process' || el.localName === 'subProcess'
    );

    if (processEls.length === 0) {
        return { processes: [], warnings: ['No <process> definitions found in BPMN file.'] };
    }

    const processes = processEls.map(el => parseProcess(el, shapeIndex, warnings));
    const validProcesses = processes.filter(p => p.nodes.length > 0);

    if (validProcesses.length === 0) {
        warnings.push('All processes were empty or contained only unsupported elements.');
        return { processes, warnings };
    }

    return { processes: validProcesses, warnings };
}

/**
 * Returns true if the given text content looks like a BPMN 2.0 document.
 */
export function detectBpmnFormat(content: string): boolean {
    const head = content.slice(0, 2000);
    return (
        head.includes('http://www.omg.org/spec/BPMN') ||
        head.includes('<definitions') ||
        head.includes('<bpmn:definitions') ||
        head.includes('<bpmn2:definitions')
    );
}
